// Which parts of a player are public, and which stay with the tournament's arbiters.
//
// WHY. A public tournament's rosters (tournaments/{id}/content/players_*) are world-
// readable: the spectator pages are built from them. Until 2026-10 every player carried the
// whole of their registration there — e-mail, phone, full date of birth, their answers and
// comment, and the registration's document id, which was also the capability that cancels
// it. Anyone could read those for every pupil in every public tournament, and cancel them.
// The privacy policy promises the opposite (js/ui/privacy-policy.js).
//
// So a roster is stored in two halves: the public list, without these fields, in
// content/players_{g}; and one entry per player that has any of them, matched by player id,
// in tournaments/{id}/private/players_{g}, which only the tournament's arbiters can read
// (firestore.rules). The app works on the merged roster exactly as before; only the
// storage layer (js/services/cloud-sync.js) splits and merges.
//
// The SAME list is in functions/lib/player-privacy.js for the server's own roster writes
// (auto-import, the waiting list); tests/player-privacy.test.js keeps the two equal.

export const PRIVATE_PLAYER_FIELDS = Object.freeze([
    'registrationId',       // the registration's id — once the cancel capability
    'email',
    'phone',
    'birthDate',            // the full date; the year (birthYear) stays public
    'registrationAnswers',  // the organiser's own questions
    'registrationComment',
    'nsfId',                // the NSF membership number: the arbiters' link to NSF's register
    'nsfVerifiedYear'       // the year NSF's register showed them paid, when they registered
]);

const PRIVATE = new Set(PRIVATE_PLAYER_FIELDS);

function hasValue(v) {
    if (v === null || v === undefined || v === '') return false;
    if (typeof v === 'object' && !Array.isArray(v)) return Object.keys(v).length > 0;
    return true;
}

/**
 * Split a roster into what spectators may read and what only the arbiters may.
 *
 * @param {object[]} players
 * @returns {{ publicPlayers: object[], privateEntries: object[] }}
 *   privateEntries: [{ id, ...the private fields that have a value }], one per player
 *   with anything private to keep, in roster order.
 */
export function splitRoster(players) {
    const publicPlayers = [];
    const privateEntries = [];
    (Array.isArray(players) ? players : []).forEach(p => {
        if (!p || typeof p !== 'object') { publicPlayers.push(p); return; }
        const pub = {};
        const priv = {};
        Object.keys(p).forEach(k => {
            if (!PRIVATE.has(k)) pub[k] = p[k];
            else if (hasValue(p[k])) priv[k] = p[k];
        });
        publicPlayers.push(pub);
        if (Object.keys(priv).length > 0 && p.id !== undefined && p.id !== null) {
            privateEntries.push({ id: String(p.id), ...priv });
        }
    });
    return { publicPlayers, privateEntries };
}

/**
 * Put the private halves back onto a roster read from the cloud.
 *
 * A private entry wins over the same field on the public player, which only a roster
 * written before the split still carries (and which the next save moves across).
 *
 * @param {object[]} players          the public list
 * @param {object[]} privateEntries   from private/players_{g}, or nothing
 * @returns {object[]}
 */
export function mergeRoster(players, privateEntries) {
    const list = Array.isArray(players) ? players : [];
    const entries = Array.isArray(privateEntries) ? privateEntries : [];
    if (entries.length === 0) return list;
    const byId = new Map(entries.filter(e => e && e.id !== undefined).map(e => [String(e.id), e]));
    return list.map(p => {
        if (!p || typeof p !== 'object') return p;
        const e = byId.get(String(p.id));
        if (!e) return p;
        const { id: _id, ...fields } = e;
        return { ...p, ...fields };
    });
}

/**
 * The private entries to write when the writer never read the private document (a device
 * whose read failed, or a roster from before the split): what the cloud already holds for
 * players still on the roster, with this writer's own values laid over it. A player who is
 * no longer on the roster loses their entry, as they would have from a full write.
 *
 * @param {object[]} remoteEntries
 * @param {object[]} localEntries
 * @param {Array<string|number>} rosterIds  ids on the roster being written
 */
export function mergePrivateEntries(remoteEntries, localEntries, rosterIds) {
    const onRoster = new Set((rosterIds || []).map(String));
    const out = new Map();
    (Array.isArray(remoteEntries) ? remoteEntries : []).forEach(e => {
        if (e && onRoster.has(String(e.id))) out.set(String(e.id), { ...e, id: String(e.id) });
    });
    (Array.isArray(localEntries) ? localEntries : []).forEach(e => {
        if (!e || !onRoster.has(String(e.id))) return;
        out.set(String(e.id), { ...(out.get(String(e.id)) || {}), ...e, id: String(e.id) });
    });
    // Roster order, so the document does not churn when nothing changed.
    return (rosterIds || []).map(String).filter(id => out.has(id)).map(id => out.get(id));
}
